import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { useProductFeatures } from "./useProductFeatures";
import { useReactQueryErrorReporter } from "./use-error-reporter";
import { useCurrentUser } from "./useCurrentUser";
import {
  IMMUTABLE_RQ,
  REGION_RQ,
  fetchStaticJson,
  usePointers,
} from "../api/staticContent";
import type { PointersFile } from "../api/staticContent";
import type {
  CMSAssessment,
  CMSAward,
  CMSBanner,
  CMSBuildingBlock,
  CMSBuildingBlockActivity,
  CMSCareerClimb,
  CMSCareerCluster,
  CMSCareerPlanNextStep,
  CMSClimberFacet,
  CMSClimberFacetType,
  CMSCourse,
  CMSEducatorDownload,
  CMSHatIndex,
  CMSHatRegion,
  CMSHatTheme,
  CMSLadderFull,
  CMSMSHatDisplay,
  CMSMSRegionSalary,
  CMSPathway,
  CMSPayRange,
  CMSRegion,
  CMSReport,
  CMSShoutOutConnector,
  CMSTopic,
  CMSUploadedVideo,
  MSHatIndex,
} from "../types/cms-types";
import type { BannerData } from "@hats-and-ladders/client-common";
import type { CMSBadge } from "./useBadges";
import { usePathname } from "../routes/hooks/use-pathname";
import type {
  DistrictSettingsFile,
  DistrictSettingsProgram,
  OverridesByPathway,
} from "../types/districtSettings";
import {
  applyOverridesToHatFull,
  applyOverridesToHatIndexEntry,
  buildOverridesByPathway,
  matchDistrictProgramsClient,
} from "./districtOverrides";

// ── Wire types matching what packages/cms publishes ──────────────────────

type IndexHatEntry = {
  id: string;
  displayName: string;
  tagline: string;
  productPlans: string[];
  msTagline: string;
  msShortDescription: string;
  msPathway: string[];
  msCareerCluster: string[];
  careerArea: { id: string; name: string };
  careerCluster: Array<{
    id: string;
    name: string;
    icon?: { url: string };
  }>;
  riasecScores: {
    realistic: number;
    investigative: number;
    artistic: number;
    social: number;
    enterprising: number;
    conventional: number;
  };
  displayImage: { alt: string; sizes: { hatCard: { url: string } } };
  hatCardImage?: {
    alt?: string;
    smallSquare?: string | null;
    mediumSquare?: string | null;
  };
  previewAudio: string;
  detailPath: string;
};

type HatIndexFile = {
  planCode: string;
  publishedAt: string;
  hats: IndexHatEntry[];
};

type RegionFile = {
  regionId: string;
  regionName: string;
  regionCode: string;
  regionType: string;
  publishedAt: string;
  hats: Record<
    string,
    {
      demand: string | null;
      payRange: {
        mean: number | null;
        p25: number | null;
        p75: number | null;
      };
    }
  >;
};

type RegionSkillEntry = {
  skill: {
    id: string;
    name: string;
    skillType: string | null;
    powerskill: boolean;
    powerskillLabel: string | null;
  };
  percentage: number | null;
};

type RegionSkillsFile = {
  regionId: string;
  publishedAt: string;
  hats: Record<string, RegionSkillEntry[]>;
};

// ── Shared plan-scoped fetch + merge helpers ───────────────────────────────

/** Keys on PointersFile whose value is a planCode → S3-key map. */
type PlanScopedPointerKey = {
  [K in keyof PointersFile]: PointersFile[K] extends
    | Record<string, string>
    | undefined
    ? K
    : never;
}[keyof PointersFile] &
  string;

/**
 * Resolve the user's product plans → the deduped set of hashed S3 keys for a
 * plan-scoped artifact (via `pointers[mapKey][planCode]`), then fetch each file
 * in parallel. `queryName` is the second segment of the TanStack Query key
 * (`["s3", queryName, paths]`) — callers keep their original name so cache
 * identity is unchanged. All plan-scoped index hooks share this prefix.
 */
const usePlanScopedFiles = <T>(
  mapKey: PlanScopedPointerKey,
  queryName: string,
): { files: T[] | undefined; isLoading: boolean; error: Error | null } => {
  const { productPlans, isLoading: pfLoading } = useProductFeatures();
  const {
    pointers,
    isLoading: pointersLoading,
    error: pointersError,
  } = usePointers();

  const planCodes = useMemo<string[]>(
    () => (Array.isArray(productPlans) ? (productPlans as string[]) : []),
    [productPlans],
  );

  const paths = useMemo<string[]>(() => {
    if (!pointers) return [];
    const out: string[] = [];
    const seen = new Set<string>();
    const map = pointers[mapKey] as Record<string, string> | undefined;
    for (const code of planCodes) {
      const p = map?.[code];
      if (p && !seen.has(p)) {
        seen.add(p);
        out.push(p);
      }
    }
    return out;
  }, [pointers, planCodes, mapKey]);

  const {
    data: files,
    isLoading: filesLoading,
    error: filesError,
  } = useQuery<T[]>({
    queryKey: ["s3", queryName, paths],
    queryFn: () => Promise.all(paths.map((p) => fetchStaticJson<T>(p))),
    enabled: paths.length > 0,
    ...IMMUTABLE_RQ,
  });

  return {
    files,
    isLoading: pfLoading || pointersLoading || filesLoading,
    error: (pointersError ?? filesError ?? null) as Error | null,
  };
};

/**
 * Flatten a set of fetched files into one list, deduped by `String(id)` and
 * preserving first-seen order — the merge every plan-scoped index hook applies
 * after fetching.
 */
const mergeById = <File, Item>(
  files: File[] | undefined,
  select: (file: File) => Item[],
  idOf: (item: Item) => string | number,
): Item[] => {
  if (!files) return [];
  const seen = new Set<string>();
  const out: Item[] = [];
  for (const file of files) {
    for (const item of select(file)) {
      const id = String(idOf(item));
      if (seen.has(id)) continue;
      seen.add(id);
      out.push(item);
    }
  }
  return out;
};

// ── District settings (per-district programs + pathway overrides) ─────────

interface UseDistrictSettingsResult {
  programs: DistrictSettingsProgram[];
  overridesByPathway: OverridesByPathway;
  isLoading: boolean;
  error: Error | null;
}

/**
 * Load the current user's district settings file
 * (`district-settings/<districtId>.<hash>.json`) and expose its programs +
 * a pathway-override map. Replaces the per-district joins the AppSync
 * `cmsMSHatDisplay`/hat-index resolvers used to do server-side. When the user
 * has no district (or it isn't published), this resolves to empty data without
 * a fetch — never an error.
 */
export const useDistrictSettings = (): UseDistrictSettingsResult => {
  const user = useCurrentUser();
  const districtId = "districtId" in user ? user.districtId : undefined;
  const {
    pointers,
    isLoading: pointersLoading,
    error: pointersError,
  } = usePointers();

  const key = districtId ? pointers?.districtSettings?.[districtId] : undefined;

  const {
    data,
    isLoading: fileLoading,
    error: fileError,
  } = useQuery<DistrictSettingsFile>({
    queryKey: ["s3", "districtSettings", key],
    queryFn: () => fetchStaticJson<DistrictSettingsFile>(key as string),
    enabled: !!key,
    ...IMMUTABLE_RQ,
  });

  const overridesByPathway = useMemo(
    () => buildOverridesByPathway(data?.overrides),
    [data],
  );

  return {
    programs: data?.programs ?? [],
    overridesByPathway,
    // Only "loading" while we actually have a district key to resolve.
    isLoading: pointersLoading || (!!key && fileLoading),
    error: (pointersError ?? fileError ?? null) as Error | null,
  };
};

// ── Building blocks ──────────────────────────────────────────────────────

/**
 * Resolve the user's product plans → their hat-index files via pointers.json,
 * fetch each one in parallel, and dedupe entries by hat id. District pathway
 * overrides (if any) are applied to each entry's `msPathway`/`msCareerCluster`
 * here — the single chokepoint feeding every hat-index consumer.
 */
const useUserHatIndexEntries = () => {
  const { files, isLoading, error } = usePlanScopedFiles<HatIndexFile>(
    "hatIndex",
    "hatIndex",
  );
  const { overridesByPathway } = useDistrictSettings();

  const entries = useMemo<IndexHatEntry[]>(() => {
    const merged = mergeById(
      files,
      (f) => f.hats,
      (e) => e.id,
    );
    if (overridesByPathway.size === 0) return merged;
    return merged.map((e) =>
      applyOverridesToHatIndexEntry(e, overridesByPathway),
    );
  }, [files, overridesByPathway]);

  return {
    entries,
    detailPathByHatId: useMemo(() => {
      const m = new Map<string, string>();
      for (const e of entries) m.set(e.id, e.detailPath);
      return m;
    }, [entries]),
    isLoading,
    error,
  };
};

// ── Shape adapters: published wire shape → existing CMS type shape ───────

const toMSHatIndex = (e: IndexHatEntry): MSHatIndex => ({
  id: e.id,
  displayName: e.displayName,
  tagline: e.tagline,
  productPlans: e.productPlans,
  msShortDescription: e.msShortDescription,
  msPathway: e.msPathway,
  msTagline: e.msTagline,
  previewAudio: e.previewAudio,
  msCareerCluster: e.msCareerCluster,
  careerArea: e.careerArea,
  riasecScores: e.riasecScores,
  displayImage: e.displayImage,
});

const toCMSHatIndex = (e: IndexHatEntry): CMSHatIndex => ({
  ...toMSHatIndex(e),
  careerCluster: e.careerCluster as unknown as CMSHatIndex["careerCluster"],
});

const toCMSPayRange = (
  pr: RegionFile["hats"][string]["payRange"],
): CMSPayRange => ({
  twentyFifthPercentile: pr.p25 ?? 0,
  mean: pr.mean ?? 0,
  seventyFifthPercentile: pr.p75 ?? 0,
});

const regionFromFile = (file: RegionFile): CMSRegion =>
  ({
    id: file.regionId,
    name: file.regionName,
    code: file.regionCode ?? "",
    type: file.regionType,
    active: true,
    displayOrder: 0,
    updatedAt: file.publishedAt,
    createdAt: file.publishedAt,
  }) as CMSRegion;

// ── Public hooks (parallel to useCMSData.ts) ─────────────────────────────

interface UseHatIndexResult {
  hatIndex: MSHatIndex[];
  loading: boolean;
  error: Error | null;
}

export const useMSHatIndex = (): UseHatIndexResult => {
  const { entries, isLoading, error } = useUserHatIndexEntries();
  useReactQueryErrorReporter("Could not load the MS hat index", error);
  const hatIndex = useMemo(() => entries.map(toMSHatIndex), [entries]);
  return {
    hatIndex,
    loading: isLoading,
    error: (error as Error | null) ?? null,
  };
};

interface UseFullHatIndexResult {
  hatIndex: CMSHatIndex[];
  allHats: CMSHatIndex[];
  loading: boolean;
  error: Error | null;
}

export const useHatIndex = (): UseFullHatIndexResult => {
  const { entries, isLoading, error } = useUserHatIndexEntries();
  useReactQueryErrorReporter("Could not load the hat index", error);
  const hatIndex = useMemo(() => entries.map(toCMSHatIndex), [entries]);
  return {
    hatIndex,
    allHats: hatIndex,
    loading: isLoading,
    error: (error as Error | null) ?? null,
  };
};

// ── Hat card images (by-id lookup) ───────────────────────────────────────

export interface UseHatCardImagesResult {
  map: Map<
    string,
    { alt?: string; smallSquare?: string; mediumSquare?: string }
  >;
  isLoading: boolean;
}

/**
 * Static equivalent of useHatCardImages. The square crops live on each hat's
 * index entry, so this is a pure lookup over the already-loaded index — no
 * extra fetches. Coverage matches the GraphQL hook: ids outside the user's
 * published index resolve to no image (same as the detail-file path, which is
 * itself index-derived).
 */
export const useHatCardImages = (hatIds: string[]): UseHatCardImagesResult => {
  const { entries, isLoading, error } = useUserHatIndexEntries();
  useReactQueryErrorReporter("Could not load static hat card images", error);
  const map = useMemo(() => {
    const next = new Map<
      string,
      { alt?: string; smallSquare?: string; mediumSquare?: string }
    >();
    if (hatIds.length === 0) return next;
    const wanted = new Set(hatIds);
    for (const e of entries) {
      if (!wanted.has(e.id) || !e.hatCardImage) continue;
      next.set(e.id, {
        alt: e.hatCardImage.alt ?? undefined,
        smallSquare: e.hatCardImage.smallSquare ?? undefined,
        mediumSquare: e.hatCardImage.mediumSquare ?? undefined,
      });
    }
    return next;
  }, [entries, hatIds]);
  return { map, isLoading: hatIds.length > 0 && isLoading };
};

// ── Region hooks ─────────────────────────────────────────────────────────

interface UseHatRegionsContentResult {
  hatRegions: CMSHatRegion[];
  loading: boolean;
  error: Error | null;
}

const useRegionFile = (regionId: string | undefined) =>
  useQuery<RegionFile>({
    queryKey: ["s3", "region", regionId],
    queryFn: () => fetchStaticJson<RegionFile>(`regions/${regionId}.json`),
    enabled: !!regionId,
    ...REGION_RQ,
  });

const useHatDetail = (
  hatId: string | undefined,
  detailPath: string | undefined,
) =>
  useQuery<Record<string, unknown>>({
    queryKey: ["s3", "hatDetail", detailPath],
    queryFn: () =>
      fetchStaticJson<Record<string, unknown>>(detailPath as string),
    enabled: !!hatId && !!detailPath,
    ...IMMUTABLE_RQ,
  });

/** United States — see packages/cms preview hat page ("National = region ID 1"). */
const NATIONAL_REGION_ID = "1";

const useRegionSkillsFile = (regionId: string | undefined, enabled: boolean) =>
  useQuery<RegionSkillsFile>({
    queryKey: ["s3", "regionSkills", regionId],
    queryFn: () =>
      fetchStaticJson<RegionSkillsFile>(`region-skills/${regionId}.json`),
    enabled: enabled && !!regionId,
    ...REGION_RQ,
  });

const stateRegionIdOf = (stateId: number | string | undefined): string =>
  stateId != null && Number(stateId) > 0 ? String(stateId) : "36";
const msaRegionIdOf = (
  msaId: number | string | undefined,
): string | undefined =>
  msaId != null && Number(msaId) > 0 ? String(msaId) : undefined;

interface UseRegionSkillsResult {
  /** hatId → that hat's regional skill rows for this region. */
  skillsByHatId: Record<string, RegionSkillEntry[]>;
  regionName: string | undefined;
  loading: boolean;
  error: Error | null;
}

/**
 * All hats' regional skills for a single region, from the published
 * `region-skills/<regionId>.json` artifact (one fetch covers every hat), plus
 * the region's display name from `regions/<regionId>.json`. Replaces per-hat
 * `cmsHatsRegions` calls for skill-only views (e.g. the Hats debug page).
 */
export const useRegionSkills = (
  regionId: string | undefined,
): UseRegionSkillsResult => {
  const region = useRegionFile(regionId);
  const skills = useRegionSkillsFile(regionId, !!regionId);
  const error =
    (region.error as Error | null) ?? (skills.error as Error | null) ?? null;
  useReactQueryErrorReporter("Could not load static region skills", error);
  return {
    skillsByHatId: skills.data?.hats ?? {},
    regionName: region.data?.regionName,
    loading: region.isLoading || skills.isLoading,
    error,
  };
};

/**
 * Shared multi-region assembler for the hat-detail content hooks. Fetches a
 * FIXED set of region files — USA (national), state, msa — so the hook count is
 * stable. When `includeSkills`, it also fetches the matching per-region skills
 * files (the sunsettable `region-skills/<regionId>.json` artifact) and attaches
 * each entry's `skills` (looked up by hat id) to the region entry; callers that
 * don't need skills (V8, hat-only planner steps) leave it off so those files are
 * never requested. Returns one CMSHatRegion per region that has a row for the hat
 * (msa, then state, then national).
 */
const useHatRegionRows = (
  hatId: string | undefined,
  stateId: number | string | undefined,
  msaId: number | string | undefined,
  includeSkills: boolean,
): UseHatRegionsContentResult => {
  const {
    detailPathByHatId,
    isLoading: indexLoading,
    error: indexError,
  } = useUserHatIndexEntries();
  const detailPath = hatId ? detailPathByHatId.get(hatId) : undefined;
  const {
    data: hatDoc,
    isLoading: detailLoading,
    error: detailError,
  } = useHatDetail(hatId, detailPath);

  const natId = hatId ? NATIONAL_REGION_ID : undefined;
  const stId = hatId ? stateRegionIdOf(stateId) : undefined;
  const msaId2 = hatId ? msaRegionIdOf(msaId) : undefined;

  const nat = useRegionFile(natId);
  const st = useRegionFile(stId);
  const msa = useRegionFile(msaId2);
  const natSkills = useRegionSkillsFile(natId, includeSkills);
  const stSkills = useRegionSkillsFile(stId, includeSkills);
  const msaSkills = useRegionSkillsFile(msaId2, includeSkills);

  const error =
    (indexError as Error | null) ??
    (nat.error as Error | null) ??
    (st.error as Error | null) ??
    (msa.error as Error | null) ??
    (detailError as Error | null) ??
    null;
  useReactQueryErrorReporter("Could not load static hat regional data", error);

  const hatRegions = useMemo<CMSHatRegion[]>(() => {
    if (!hatId || !hatDoc) return [];
    const slots: Array<{
      file: RegionFile | undefined;
      skills: RegionSkillsFile | undefined;
    }> = [
      { file: msa.data, skills: msaSkills.data },
      { file: st.data, skills: stSkills.data },
      { file: nat.data, skills: natSkills.data },
    ];
    const out: CMSHatRegion[] = [];
    const seen = new Set<string>();
    for (const { file, skills } of slots) {
      if (!file || seen.has(file.regionId)) continue;
      const row = file.hats[hatId];
      if (!row) continue;
      seen.add(file.regionId);
      out.push({
        id: `${file.regionId}::${hatId}`,
        hat: hatDoc as unknown as CMSHatRegion["hat"],
        region: regionFromFile(file),
        demand: row.demand ?? undefined,
        payRange: toCMSPayRange(row.payRange),
        ...(includeSkills
          ? {
              skills: (skills?.hats[hatId] ??
                []) as unknown as CMSHatRegion["skills"],
            }
          : {}),
        updatedAt: file.publishedAt,
        createdAt: file.publishedAt,
      } as CMSHatRegion);
    }
    return out;
  }, [
    hatId,
    hatDoc,
    msa.data,
    st.data,
    nat.data,
    msaSkills.data,
    stSkills.data,
    natSkills.data,
    includeSkills,
  ]);

  return {
    hatRegions,
    loading:
      indexLoading ||
      detailLoading ||
      nat.isLoading ||
      st.isLoading ||
      msa.isLoading ||
      (includeSkills &&
        (natSkills.isLoading || stSkills.isLoading || msaSkills.isLoading)),
    error,
  };
};

type HatRegionsOpts = { includeSkills?: boolean };

/**
 * Static equivalent of useHatRegionsContent. Returns CMSHatRegion[] across the
 * national + state + msa regions that have data for the hat. Pass
 * `{ includeSkills: true }` to also load the regional-skills artifact (WF + V7
 * hat-detail skills sections, planner skill steps); omit it otherwise.
 */
export const useHatRegionsContent = (
  hatId: string | undefined,
  stateId?: number | string,
  msaId?: number | string,
  opts?: HatRegionsOpts,
): UseHatRegionsContentResult =>
  useHatRegionRows(hatId, stateId, msaId, opts?.includeSkills === true);

/**
 * Static equivalent of useHatRegionsMSContent. Identical to
 * `useHatRegionsContent` — the hat detail file already carries all `ms*`
 * fields. Kept as a separate export to match the existing hook surface 1:1.
 */
export const useHatRegionsMSContent = useHatRegionsContent;

// ── useMSHatDisplay (consolidated hat + salary + district programs) ─

interface UseMSHatDisplayResult {
  hatDisplay: CMSMSHatDisplay | null;
  loading: boolean;
  error: Error | null;
}

const toRegionSalary = (
  file: RegionFile | undefined,
  row: RegionFile["hats"][string] | undefined,
): CMSMSRegionSalary | undefined => {
  if (!file || !row) return undefined;
  return {
    regionName: file.regionName,
    regionType: file.regionType,
    demand: row.demand ?? undefined,
    payRange: toCMSPayRange(row.payRange),
  };
};

/**
 * Static equivalent of useMSHatDisplay. Builds a `CMSMSHatDisplay`-shaped
 * object from the published hat detail file + region files, then applies the
 * user's district pathway overrides and computes `districtPrograms` from the
 * per-district settings file — the work the AppSync `cmsMSHatDisplay` resolver
 * used to do server-side. `middleSchoolId` filters program schools to the
 * student's feeder high schools.
 */
export const useMSHatDisplay = (
  hatId: string | undefined,
  stateId?: string | number,
  msaId?: string | number,
  middleSchoolId?: string,
): UseMSHatDisplayResult => {
  const stId = hatId ? stateRegionIdOf(stateId) : undefined;
  const msaId2 = hatId ? msaRegionIdOf(msaId) : undefined;

  const {
    programs: districtProgramDefs,
    overridesByPathway,
    isLoading: districtLoading,
    error: districtError,
  } = useDistrictSettings();

  const {
    detailPathByHatId,
    isLoading: indexLoading,
    error: indexError,
  } = useUserHatIndexEntries();
  const detailPath = hatId ? detailPathByHatId.get(hatId) : undefined;

  const {
    data: hatDoc,
    isLoading: detailLoading,
    error: detailError,
  } = useHatDetail(hatId, detailPath);
  const {
    data: msaRegion,
    isLoading: msaRegionLoading,
    error: msaRegionError,
  } = useRegionFile(msaId2);
  const {
    data: stateRegion,
    isLoading: stateRegionLoading,
    error: stateRegionError,
  } = useRegionFile(stId);
  const {
    data: nationalRegion,
    isLoading: natRegionLoading,
    error: natRegionError,
  } = useRegionFile(hatId ? NATIONAL_REGION_ID : undefined);

  const hatDisplay = useMemo<CMSMSHatDisplay | null>(() => {
    if (!hatId || !hatDoc) return null;
    // Apply district pathway overrides to the (object-form) msPathway /
    // msCareerCluster before reading them, matching the resolver order.
    const detail = applyOverridesToHatFull(
      { ...(hatDoc as Record<string, unknown>), id: hatId } as {
        id: string;
        msPathway?: unknown[] | null;
        msCareerCluster?: unknown[] | null;
      },
      overridesByPathway,
    ) as Record<string, unknown>;
    const themes = (detail.msThemes as unknown[] | undefined) ?? [];

    // Derive the post-override pathway id set, then match district programs.
    const hatPathwayIds = new Set<string>(
      ((detail.msPathway as unknown[] | undefined) ?? []).map((p) =>
        String(p && typeof p === "object" ? (p as { id: unknown }).id : p),
      ),
    );
    const districtPrograms = matchDistrictProgramsClient(
      districtProgramDefs,
      hatId,
      hatPathwayIds,
      middleSchoolId,
    );
    // Selected region = MSA when the hat has MSA data, otherwise STATE.
    const msaRow = msaRegion?.hats?.[hatId];
    const stateRow = stateRegion?.hats?.[hatId];
    const nationalRow = nationalRegion?.hats?.[hatId];
    const selectedRegion = msaRow ? msaRegion : stateRegion;
    const selectedRow = msaRow ?? stateRow;

    // Fall back to the top-level `careerArea` when `ms.careerArea` is unset
    // — many hats only have the regular field filled, and the v8 also-like
    // fallback in useMSHatV2Data.ts keys off `display.msCareerArea?.name`.
    type CareerArea = { id?: string; name?: string } | null | undefined;
    const msCareerArea = detail.msCareerArea as CareerArea;
    const careerArea = detail.careerArea as CareerArea;
    const effectiveMsCareerArea =
      msCareerArea && msCareerArea.name
        ? msCareerArea
        : (careerArea ?? msCareerArea);

    return {
      ...(detail as object),
      id: hatId,
      msCareerArea: effectiveMsCareerArea,
      // CMSMSHatDisplay declares msThemes as string[]; the published file
      // stores [{id, name}]. Extract ids to match the type contract,
      // skipping any malformed entries that lack a string id.
      msThemes: themes
        .map((t) =>
          t && typeof t === "object" ? (t as { id?: unknown }).id : t,
        )
        .filter((id): id is string => typeof id === "string"),
      msPersonalityBadges: undefined,
      selectedRegionSalary: toRegionSalary(selectedRegion, selectedRow),
      nationalSalary: toRegionSalary(nationalRegion, nationalRow),
      districtPrograms,
    } as unknown as CMSMSHatDisplay;
  }, [
    hatId,
    hatDoc,
    msaRegion,
    stateRegion,
    nationalRegion,
    overridesByPathway,
    districtProgramDefs,
    middleSchoolId,
  ]);

  const error =
    (indexError as Error | null) ??
    (detailError as Error | null) ??
    (msaRegionError as Error | null) ??
    (stateRegionError as Error | null) ??
    (natRegionError as Error | null) ??
    (districtError as Error | null) ??
    null;
  useReactQueryErrorReporter(
    "Could not load static MS hat display data",
    error,
  );

  return {
    hatDisplay,
    loading:
      indexLoading ||
      detailLoading ||
      msaRegionLoading ||
      stateRegionLoading ||
      natRegionLoading ||
      districtLoading,
    error,
  };
};

// ── Pathways + Career Clusters ───────────────────────────────────────────
//
// Mirrors the hat index/detail split: a per-plan combined index
// (`cluster-pathway-index.<plan>.<hash>.json`, keyed in pointers under
// `clusterPathwayIndexes`) plus per-id detail files (`pathways/<id>.<hash>.json`,
// `clusters/<id>.<hash>.json`). The index groups pathways under their clusters
// exactly like the GraphQL `usePathways` derivation.

type PathwayIndexEntry = {
  id: string;
  name: string;
  displayImage?: { alt: string; url: string };
  detailPath: string;
};

type ClusterPathwayIndexEntry = {
  id: string;
  name: string;
  colorTheme: string | null;
  riasecScores: IndexHatEntry["riasecScores"];
  icon?: { url: string };
  displayImage?: { alt: string; url: string };
  detailPath: string;
  pathways: PathwayIndexEntry[];
};

type ClusterPathwayIndexFile = {
  planCode: string;
  publishedAt: string;
  clusters: ClusterPathwayIndexEntry[];
};

export interface ClusterWithPathways {
  cluster: CMSCareerCluster;
  pathways: CMSPathway[];
}

/** Build a list-shaped CMSCareerCluster from an index entry (no content/intro
 * — those live in the detail file and aren't read by list consumers). */
const clusterFromIndex = (e: ClusterPathwayIndexEntry): CMSCareerCluster =>
  ({
    id: e.id,
    name: e.name,
    icon: e.icon ?? { url: "" },
    displayImage: e.displayImage ?? null,
    introText: "",
    colorTheme: e.colorTheme ?? "",
    riasecScores: e.riasecScores,
    content: [],
  }) as unknown as CMSCareerCluster;

/**
 * Resolve the user's product plans → their combined cluster+pathway index
 * files via pointers.json, fetch them in parallel, and reduce into the shapes
 * the GraphQL hooks returned: `clusters` (cluster → its pathways), a deduped
 * flat `cmsPathways`, and id→detailPath maps for the detail hooks.
 */
const useUserClusterPathwayIndex = () => {
  const { files, isLoading, error } =
    usePlanScopedFiles<ClusterPathwayIndexFile>(
      "clusterPathwayIndexes",
      "clusterPathwayIndexes",
    );

  const reduced = useMemo(() => {
    const clusterOrder: string[] = [];
    const clusterEntryById = new Map<string, ClusterPathwayIndexEntry>();
    const clusterPathwayIds = new Map<string, string[]>();
    const pathwayEntryById = new Map<string, PathwayIndexEntry>();
    const pathwayClusterIds = new Map<string, string[]>();
    const pathwayDetailPathById = new Map<string, string>();
    const clusterDetailPathById = new Map<string, string>();

    for (const file of files ?? []) {
      for (const c of file.clusters) {
        if (!clusterEntryById.has(c.id)) {
          clusterEntryById.set(c.id, c);
          clusterPathwayIds.set(c.id, []);
          clusterDetailPathById.set(c.id, c.detailPath);
          clusterOrder.push(c.id);
        }
        const pathwayIds = clusterPathwayIds.get(c.id)!;
        for (const p of c.pathways) {
          if (!pathwayEntryById.has(p.id)) {
            pathwayEntryById.set(p.id, p);
            pathwayDetailPathById.set(p.id, p.detailPath);
          }
          if (!pathwayIds.includes(p.id)) pathwayIds.push(p.id);
          const cids = pathwayClusterIds.get(p.id) ?? [];
          if (!cids.includes(c.id)) cids.push(c.id);
          pathwayClusterIds.set(p.id, cids);
        }
      }
    }

    const clusterRefById = new Map<string, CMSCareerCluster>();
    for (const cid of clusterOrder) {
      clusterRefById.set(cid, clusterFromIndex(clusterEntryById.get(cid)!));
    }

    const pathwayById = new Map<string, CMSPathway>();
    for (const [pid, p] of pathwayEntryById) {
      pathwayById.set(pid, {
        id: p.id,
        name: p.name,
        introText: "",
        productPlan: [],
        displayImage: p.displayImage ?? null,
        content: [],
        careerCluster: (pathwayClusterIds.get(pid) ?? [])
          .map((cid) => clusterRefById.get(cid))
          .filter((c): c is CMSCareerCluster => !!c),
        updatedAt: "",
        createdAt: "",
      } as unknown as CMSPathway);
    }

    const clusters: ClusterWithPathways[] = clusterOrder.map((cid) => ({
      cluster: clusterRefById.get(cid)!,
      pathways: clusterPathwayIds
        .get(cid)!
        .map((pid) => pathwayById.get(pid)!)
        .filter(Boolean),
    }));

    const cmsPathways: CMSPathway[] = [];
    const seenPathway = new Set<string>();
    for (const cid of clusterOrder) {
      for (const pid of clusterPathwayIds.get(cid)!) {
        if (seenPathway.has(pid)) continue;
        seenPathway.add(pid);
        cmsPathways.push(pathwayById.get(pid)!);
      }
    }

    return {
      clusters,
      cmsPathways,
      pathwayDetailPathById,
      clusterDetailPathById,
    };
  }, [files]);

  return {
    ...reduced,
    isLoading,
    error,
  };
};

interface UsePathwaysResult {
  clusters: ClusterWithPathways[];
  cmsPathways: CMSPathway[];
  loading: boolean;
  error: Error | null;
}

export const usePathways = (): UsePathwaysResult => {
  const { clusters, cmsPathways, isLoading, error } =
    useUserClusterPathwayIndex();
  useReactQueryErrorReporter("Could not load the static pathways", error);
  return {
    clusters,
    cmsPathways,
    loading: isLoading,
    error: (error as Error | null) ?? null,
  };
};

const useDetailDoc = (
  kind: "pathwayDetail" | "clusterDetail",
  id: string | undefined,
  detailPath: string | undefined,
) =>
  useQuery<Record<string, unknown>>({
    queryKey: ["s3", kind, detailPath],
    queryFn: () =>
      fetchStaticJson<Record<string, unknown>>(detailPath as string),
    enabled: !!id && !!detailPath,
    ...IMMUTABLE_RQ,
  });

interface UsePathwayContentResult {
  pathway: CMSPathway | null | undefined;
  loading: boolean;
  error: Error | null;
}

export const usePathwayContent = (
  pathwayId: string,
): UsePathwayContentResult => {
  const {
    pathwayDetailPathById,
    isLoading: indexLoading,
    error: indexError,
  } = useUserClusterPathwayIndex();
  const detailPath = pathwayId
    ? pathwayDetailPathById.get(pathwayId)
    : undefined;
  const {
    data,
    isLoading: detailLoading,
    error: detailError,
  } = useDetailDoc("pathwayDetail", pathwayId, detailPath);

  const error =
    (indexError as Error | null) ?? (detailError as Error | null) ?? null;
  useReactQueryErrorReporter("Could not load static pathway content", error);

  return {
    pathway: (data as unknown as CMSPathway) ?? null,
    loading: indexLoading || detailLoading,
    error,
  };
};

interface UseCareerClusterResult {
  data: CMSCareerCluster | null;
  isLoading: boolean;
  error: Error | null;
}

export const useGetCareerCluster = (
  clusterId: string | undefined,
): UseCareerClusterResult => {
  const {
    clusterDetailPathById,
    isLoading: indexLoading,
    error: indexError,
  } = useUserClusterPathwayIndex();
  const detailPath = clusterId
    ? clusterDetailPathById.get(clusterId)
    : undefined;
  const {
    data,
    isLoading: detailLoading,
    error: detailError,
  } = useDetailDoc("clusterDetail", clusterId, detailPath);

  const error =
    (indexError as Error | null) ?? (detailError as Error | null) ?? null;
  useReactQueryErrorReporter("Could not load static career cluster", error);

  return {
    data: (data as unknown as CMSCareerCluster) ?? null,
    isLoading: indexLoading || detailLoading,
    error,
  };
};

// ── Learning content: courses + career climbs + ladders ───────────────────
// Wire shapes published by packages/cms (runLearningPublish / projectLearning).

type WireTopic = {
  id: string;
  name: string;
  isSubTopic: boolean;
  sortOrder: number | null;
  displayIcon: { url: string } | null;
};

type WireAssessment = {
  id: string;
  assessment: string | null;
  active: boolean;
  assessmentType: string | null;
  productPlan: Array<{ code: string }>;
};

type WireLadderDetail = {
  id: string;
  name: string;
  ladderType: string | null;
  image: { alt: string; url: string } | null;
  shortDescription: string | null;
  endScreenDescription: string | null;
  experiencePoints: number;
  estimatedTime: number;
  itemCount: number | null;
  instructionalLevel: string | null;
  topics: WireTopic[];
  assessments: WireAssessment[];
};

type WireChildRef = { id: string; order: number | null };

type WireLadderEntry = Omit<WireLadderDetail, "endScreenDescription"> & {
  productPlanCodes: string[];
  detailPath: string;
};

type WireClimbEntry = {
  id: string;
  name: string;
  reference: string | null;
  learnosityTag: string | null;
  image: { alt: string; url: string } | null;
  shortDescription: string | null;
  longDescription: string | null;
  instructionalLevel: string | null;
  learningObjectives: Array<{ objective: string | null }>;
  topics: WireTopic[];
  productPlan: Array<{ code: string }>;
  productPlanCodes: string[];
  ladders: WireChildRef[];
};

type WireCourseEntry = {
  id: string;
  name: string;
  order: number | null;
  image: { alt: string; url: string } | null;
  shortDescription: string | null;
  longDescription: string | null;
  instructionalLevel: string | null;
  estimatedCourseTime: number;
  courseType: string | null;
  learningObjectives: Array<{ objective: string | null }>;
  topics: WireTopic[];
  productPlan: Array<{ code: string }>;
  productPlanCodes: string[];
  climbs: WireChildRef[];
};

type LearningIndexFile = {
  planCode: string;
  publishedAt: string;
  courses: WireCourseEntry[];
  climbs: WireClimbEntry[];
  ladders: WireLadderEntry[];
};

// ── Wire → CMS shape adapters ───────────────────────────────────────────

const toTopic = (w: WireTopic): CMSTopic => ({
  id: w.id,
  name: w.name,
  isSubTopic: w.isSubTopic,
  sortOrder: w.sortOrder ?? undefined,
  displayIcon: w.displayIcon ?? undefined,
});

const toAssessment = (w: WireAssessment): CMSAssessment =>
  ({
    id: w.id,
    active: w.active,
    assessmentType: w.assessmentType ?? "",
    assessment: w.assessment ?? "",
    productPlan: w.productPlan,
  }) as unknown as CMSAssessment;

const ladderFromDetail = (w: WireLadderDetail): CMSLadderFull =>
  ({
    id: w.id,
    name: w.name,
    ladderType: w.ladderType ?? undefined,
    image: w.image ?? undefined,
    shortDescription: w.shortDescription,
    endScreenDescription: w.endScreenDescription,
    experiencePoints: w.experiencePoints,
    estimatedTime: w.estimatedTime,
    itemCount: w.itemCount ?? undefined,
    instructionalLevel: w.instructionalLevel ?? undefined,
    topics: w.topics.map(toTopic),
    assessments: w.assessments.map(toAssessment),
    updatedAt: "",
    createdAt: "",
  }) as unknown as CMSLadderFull;

/** Slim index entry → CMSLadderFull (no endScreenDescription — that's detail). */
const ladderFromEntry = (
  w: WireLadderEntry,
  order?: number | null,
): CMSLadderFull =>
  ({
    id: w.id,
    name: w.name,
    ladderType: w.ladderType ?? undefined,
    image: w.image ?? undefined,
    shortDescription: w.shortDescription,
    experiencePoints: w.experiencePoints,
    estimatedTime: w.estimatedTime,
    itemCount: w.itemCount ?? undefined,
    instructionalLevel: w.instructionalLevel ?? undefined,
    topics: w.topics.map(toTopic),
    assessments: w.assessments.map(toAssessment),
    ...(order != null ? { order } : {}),
    updatedAt: "",
    createdAt: "",
  }) as unknown as CMSLadderFull;

/**
 * Resolve the user's product plans → their learning index files via
 * pointers.json, fetch in parallel, and dedupe entries by id (mirrors
 * useUserHatIndexEntries / useUserClusterPathwayIndex).
 */
const useUserLearningIndex = () => {
  const { files, isLoading, error } = usePlanScopedFiles<LearningIndexFile>(
    "learningIndexes",
    "learningIndexes",
  );

  const reduced = useMemo(() => {
    const ladderEntryById = new Map<string, WireLadderEntry>();
    const climbEntryById = new Map<string, WireClimbEntry>();
    const courseEntryById = new Map<string, WireCourseEntry>();
    const ladderOrder: string[] = [];
    const climbOrder: string[] = [];
    const courseOrder: string[] = [];

    for (const file of files ?? []) {
      for (const l of file.ladders)
        if (!ladderEntryById.has(l.id)) {
          ladderEntryById.set(l.id, l);
          ladderOrder.push(l.id);
        }
      for (const c of file.climbs)
        if (!climbEntryById.has(c.id)) {
          climbEntryById.set(c.id, c);
          climbOrder.push(c.id);
        }
      for (const c of file.courses)
        if (!courseEntryById.has(c.id)) {
          courseEntryById.set(c.id, c);
          courseOrder.push(c.id);
        }
    }

    const climbFromEntry = (w: WireClimbEntry): CMSCareerClimb =>
      ({
        id: w.id,
        name: w.name,
        learnosityTag: w.learnosityTag ?? undefined,
        image: w.image ?? undefined,
        shortDescription: w.shortDescription,
        longDescription: w.longDescription,
        learningObjectives: w.learningObjectives,
        instructionalLevel: w.instructionalLevel ?? "",
        productPlan: w.productPlan,
        topics: w.topics.map(toTopic),
        reference: w.reference ?? "",
        ladders: w.ladders.flatMap((ref) => {
          const le = ladderEntryById.get(ref.id);
          return le ? [ladderFromEntry(le, ref.order)] : [];
        }),
        updatedAt: "",
        createdAt: "",
      }) as unknown as CMSCareerClimb;

    const courseFromEntry = (w: WireCourseEntry): CMSCourse =>
      ({
        id: w.id,
        order: w.order ?? undefined,
        name: w.name,
        image: w.image ?? undefined,
        shortDescription: w.shortDescription,
        longDescription: w.longDescription,
        learningObjectives: w.learningObjectives,
        instructionalLevel: w.instructionalLevel ?? "",
        productPlan: w.productPlan,
        topics: w.topics.map(toTopic),
        estimatedCourseTime: w.estimatedCourseTime,
        courseType: w.courseType ?? "",
        climbs: w.climbs.flatMap((ref) => {
          const ce = climbEntryById.get(ref.id);
          if (!ce) return [];
          return [{ ...climbFromEntry(ce), order: ref.order ?? undefined }];
        }),
        updatedAt: "",
        createdAt: "",
      }) as unknown as CMSCourse;

    const ladders = ladderOrder.map((id) =>
      ladderFromEntry(ladderEntryById.get(id)!),
    );
    const climbs = climbOrder.map((id) =>
      climbFromEntry(climbEntryById.get(id)!),
    );
    const courses = courseOrder.map((id) =>
      courseFromEntry(courseEntryById.get(id)!),
    );

    const ladderDetailPathById = new Map<string, string>();
    for (const [id, e] of ladderEntryById)
      ladderDetailPathById.set(id, e.detailPath);

    return { ladders, climbs, courses, ladderDetailPathById };
  }, [files]);

  return {
    ...reduced,
    isLoading,
    error,
  };
};

interface UseLaddersContentResult {
  ladderContent: CMSLadderFull[];
  allLadders: CMSLadderFull[];
  loading: boolean;
  error: Error | null;
}

export const useLaddersContent = (
  params: {
    topicFilter?: { name: string }[];
    subTopicFilter?: { name: string }[];
  } = {},
): UseLaddersContentResult => {
  const { topicFilter = [], subTopicFilter = [] } = params;
  const { ladders, isLoading, error } = useUserLearningIndex();
  useReactQueryErrorReporter("Could not load the static ladders", error);

  const hasFilters = topicFilter.length > 0 || subTopicFilter.length > 0;
  const ladderContent = useMemo(() => {
    if (!hasFilters) return ladders;
    const wanted = topicFilter.map((t) => t.name);
    const wantedSub = subTopicFilter.map((t) => t.name);
    return ladders.filter((ladder) => {
      const topics = (ladder.topics ?? [])
        .filter((t) => !t.isSubTopic)
        .map((t) => t.name);
      const subTopics = (ladder.topics ?? [])
        .filter((t) => t.isSubTopic)
        .map((t) => t.name);
      return (
        wanted.some((tf) => topics.includes(tf)) ||
        wantedSub.some((stf) => subTopics.includes(stf))
      );
    });
  }, [ladders, topicFilter, subTopicFilter]);

  return {
    ladderContent,
    allLadders: ladders,
    loading: isLoading,
    error: (error as Error | null) ?? null,
  };
};

interface UseLadderContentResult {
  ladder: CMSLadderFull | null | undefined;
  loading: boolean;
  error: Error | null;
}

const useLadderDetailQuery = (detailPath: string | undefined) =>
  useQuery<WireLadderDetail>({
    queryKey: ["s3", "ladderDetail", detailPath],
    queryFn: () => fetchStaticJson<WireLadderDetail>(detailPath as string),
    enabled: !!detailPath,
    ...IMMUTABLE_RQ,
  });

export const useLadderContent = (id: string): UseLadderContentResult => {
  const {
    ladderDetailPathById,
    isLoading: indexLoading,
    error: indexError,
  } = useUserLearningIndex();
  const detailPath = id ? ladderDetailPathById.get(id) : undefined;
  const {
    data,
    isLoading: detailLoading,
    error: detailError,
  } = useLadderDetailQuery(detailPath);

  const error =
    (indexError as Error | null) ?? (detailError as Error | null) ?? null;
  useReactQueryErrorReporter("Could not load the static ladder", error);

  return {
    ladder: data ? ladderFromDetail(data) : data === null ? null : undefined,
    loading: indexLoading || detailLoading,
    error,
  };
};

/**
 * Fetch a single ladder detail file directly by its published key. Used where
 * the detailPath is known up-front (e.g. a pathway `ladderBlock`) rather than
 * resolved through the learning index.
 */
export const useLadderDetail = (
  detailPath: string | undefined,
): UseLadderContentResult => {
  const { data, isLoading, error } = useLadderDetailQuery(detailPath);
  useReactQueryErrorReporter("Could not load the static ladder", error);
  return {
    ladder: data ? ladderFromDetail(data) : data === null ? null : undefined,
    loading: isLoading,
    error: (error as Error | null) ?? null,
  };
};

interface UseCareerClimbsContentResult {
  careerClimbs: CMSCareerClimb[];
  allCareerClimbs: CMSCareerClimb[];
  loading: boolean;
  error: Error | null;
}

export const useCareerClimbsContent = (): UseCareerClimbsContentResult => {
  const { climbs, isLoading, error } = useUserLearningIndex();
  useReactQueryErrorReporter("Could not load the static climbs", error);
  return {
    careerClimbs: climbs,
    allCareerClimbs: climbs,
    loading: isLoading,
    error: (error as Error | null) ?? null,
  };
};

interface UseCareerClimbContentResult {
  careerClimb: CMSCareerClimb | null | undefined;
  loading: boolean;
  error: Error | null;
}

export const useCareerClimbContent = (
  id: string,
): UseCareerClimbContentResult => {
  const { climbs, isLoading, error } = useUserLearningIndex();
  useReactQueryErrorReporter("Could not load the static climb", error);
  const careerClimb = useMemo(
    () => (id ? (climbs.find((c) => c.id === id) ?? null) : null),
    [climbs, id],
  );
  return {
    careerClimb: isLoading ? undefined : careerClimb,
    loading: isLoading,
    error: (error as Error | null) ?? null,
  };
};

interface UseCoursesContentResult {
  courses: CMSCourse[];
  allCourses: CMSCourse[];
  loading: boolean;
  error: Error | null;
}

export const useCoursesContent = (): UseCoursesContentResult => {
  const { courses, isLoading, error } = useUserLearningIndex();
  useReactQueryErrorReporter("Could not load the static courses", error);
  return {
    courses,
    allCourses: courses,
    loading: isLoading,
    error: (error as Error | null) ?? null,
  };
};

// ── Perks & quirks (global by-id lookup) ──────────────────────────────────

export interface CMSPerkQuirkSummary {
  id: string;
  name: string;
  type: "perk" | "quirk" | null;
}

export interface UsePerksAndQuirksByIdsResult {
  map: Map<string, CMSPerkQuirkSummary>;
  isLoading: boolean;
}

type PerksQuirksFile = {
  publishedAt: string;
  perksQuirks: CMSPerkQuirkSummary[];
};

/**
 * Static replacement for the old GraphQL `usePerksAndQuirksByIds`. Loads the
 * single global `perks-quirks.<hash>.json` file (pointed to by
 * `pointers.json#perksQuirks`) and builds a by-id lookup map for the requested
 * ids. Same return shape as the old hook.
 */
export const usePerksAndQuirksByIds = (
  ids: string[],
): UsePerksAndQuirksByIdsResult => {
  const { pointers } = usePointers();
  const key = pointers?.perksQuirks;
  const { data, isLoading } = useQuery<PerksQuirksFile>({
    queryKey: ["s3", "perksQuirks", key],
    queryFn: () => fetchStaticJson<PerksQuirksFile>(key as string),
    enabled: !!key,
    ...IMMUTABLE_RQ,
  });
  const map = useMemo(() => {
    const next = new Map<string, CMSPerkQuirkSummary>();
    if (ids.length === 0) return next;
    const wanted = new Set(ids.map(String));
    for (const pq of data?.perksQuirks ?? []) {
      if (pq?.id && wanted.has(String(pq.id))) next.set(String(pq.id), pq);
    }
    return next;
  }, [data, ids]);
  return { map, isLoading: ids.length > 0 && isLoading };
};

// ── Hat themes / hat packs (plan-scoped) ──────────────────────────────────

type HatThemesIndexFile = {
  planCode: string;
  publishedAt: string;
  hatThemes: CMSHatTheme[];
};

export interface UseHatThemesResult {
  themes: CMSHatTheme[];
  allThemes: CMSHatTheme[];
  loading: boolean;
  error: Error | null;
}

/**
 * Static replacement for the GraphQL `useHatThemes`. Resolves the user's
 * product plans → their per-plan hat-themes index files
 * (`pointers.json#hatThemeIndexes[plan]`), fetches them in parallel, and
 * merges/dedupes by id. `allThemes` is the full merged list; `themes` is
 * filtered to `themeIds` when provided. Same return shape as the old hook.
 */
export const useHatThemes = (
  themeIds: Array<string | number> = [],
): UseHatThemesResult => {
  const { files, isLoading, error } = usePlanScopedFiles<HatThemesIndexFile>(
    "hatThemeIndexes",
    "hatThemeIndexes",
  );

  useReactQueryErrorReporter("Error fetching hat themes", error, false);

  const allThemes = useMemo<CMSHatTheme[]>(
    () =>
      mergeById(
        files,
        (f) => f.hatThemes,
        (t) => t.id,
      ),
    [files],
  );

  const themes = useMemo<CMSHatTheme[]>(() => {
    if (!themeIds || themeIds.length === 0) return allThemes;
    const wanted = new Set(themeIds.map(String));
    return allThemes.filter((t) => wanted.has(String(t.id)));
  }, [allThemes, themeIds]);

  return {
    themes,
    allThemes,
    loading: isLoading,
    error,
  };
};

// ── Topics (global) ────────────────────────────────────────────────────────

type TopicsFile = { publishedAt: string; topics: CMSTopic[] };

export interface UseTopicsResult {
  topics: string[];
  subTopics: string[];
  cmsTopics: CMSTopic[];
  loading: boolean;
  error: Error | null;
}

/** Static replacement for `useTopics` — splits by `isSubTopic` like the old hook. */
export const useTopics = (): UseTopicsResult => {
  const { pointers } = usePointers();
  const key = pointers?.topics;
  const { data, isLoading, error } = useQuery<TopicsFile>({
    queryKey: ["s3", "topics", key],
    queryFn: () => fetchStaticJson<TopicsFile>(key as string),
    enabled: !!key,
    ...IMMUTABLE_RQ,
  });
  useReactQueryErrorReporter("Could not load the list of topics", error, false);
  const cmsTopics = useMemo(() => data?.topics ?? [], [data]);
  const topics = useMemo(
    () => cmsTopics.filter((t) => !t.isSubTopic).map((t) => t.name),
    [cmsTopics],
  );
  const subTopics = useMemo(
    () => cmsTopics.filter((t) => t.isSubTopic).map((t) => t.name),
    [cmsTopics],
  );
  return {
    topics,
    subTopics,
    cmsTopics,
    loading: isLoading,
    error: (error as Error | null) ?? null,
  };
};

// ── Connectors / shout-out connectors (one file, shared cache) ──────────────

type ConnectorWire = CMSShoutOutConnector &
  CMSBuildingBlock & { shoutOutEnabled: boolean };
type ConnectorsFile = { publishedAt: string; connectors: ConnectorWire[] };

/**
 * Internal: fetch the single global connectors file. Both `useBuildingBlocks`
 * and `useShoutOutConnectors` call this with the SAME query key, so
 * TanStack Query dedupes to one network request + one shared cache entry.
 */
const useConnectorsFile = (): {
  connectors: ConnectorWire[];
  isLoading: boolean;
  error: Error | null;
} => {
  const { pointers } = usePointers();
  const key = pointers?.connectors;
  const { data, isLoading, error } = useQuery<ConnectorsFile>({
    queryKey: ["s3", "connectors", key],
    queryFn: () => fetchStaticJson<ConnectorsFile>(key as string),
    enabled: !!key,
    ...IMMUTABLE_RQ,
  });
  return {
    connectors: data?.connectors ?? [],
    isLoading,
    error: (error as Error | null) ?? null,
  };
};

export interface UseConnectorsResult {
  connectors: CMSBuildingBlock[];
  loading: boolean;
  error: Error | null;
}

/** Static replacement for `useBuildingBlocks` — all connectors. */
export const useBuildingBlocks = (): UseConnectorsResult => {
  const { connectors, isLoading, error } = useConnectorsFile();
  useReactQueryErrorReporter(
    "Could not load the list of Building Blocks",
    error,
    false,
  );
  return {
    connectors: connectors as unknown as CMSBuildingBlock[],
    loading: isLoading,
    error,
  };
};

export interface UseShoutOutConnectorsResult {
  connectors: CMSShoutOutConnector[];
  loading: boolean;
  error: Error | null;
}

/** Static replacement for `useShoutOutConnectors` — `shoutOutEnabled` only. */
export const useShoutOutConnectors = (): UseShoutOutConnectorsResult => {
  const { connectors, isLoading, error } = useConnectorsFile();
  useReactQueryErrorReporter(
    "Could not load the list of Shout-Out Connectors",
    error,
    false,
  );
  const filtered = useMemo(
    () => connectors.filter((c) => c.shoutOutEnabled),
    [connectors],
  );
  return {
    connectors: filtered as unknown as CMSShoutOutConnector[],
    loading: isLoading,
    error,
  };
};

// ── Connector activities (global) ───────────────────────────────────────────

type ConnectorActivityWire = {
  id: string;
  name: string;
  category: CMSBuildingBlockActivity["category"] | null;
  connectors: string[];
  updatedAt: string;
  createdAt: string;
};
type ConnectorActivitiesFile = {
  publishedAt: string;
  connectorActivities: ConnectorActivityWire[];
};

export interface UseConnectorActivitiesResult {
  connectorActivities: CMSBuildingBlockActivity[];
  loading: boolean;
  error: Error | null;
}

/** Static replacement for `useBuildingBlockActivities`. */
export const useBuildingBlockActivities = (): UseConnectorActivitiesResult => {
  const { pointers } = usePointers();
  const key = pointers?.connectorActivities;
  const { data, isLoading, error } = useQuery<ConnectorActivitiesFile>({
    queryKey: ["s3", "connectorActivities", key],
    queryFn: () => fetchStaticJson<ConnectorActivitiesFile>(key as string),
    enabled: !!key,
    ...IMMUTABLE_RQ,
  });
  useReactQueryErrorReporter(
    "Could not load the list of Building Block Activities",
    error,
    false,
  );
  const connectorActivities = useMemo<CMSBuildingBlockActivity[]>(
    () =>
      (data?.connectorActivities ?? []).map((a) => ({
        id: a.id,
        name: a.name,
        category: a.category as CMSBuildingBlockActivity["category"],
        buildingBlocks: a.connectors,
        updatedAt: a.updatedAt,
        createdAt: a.createdAt,
      })),
    [data],
  );
  return {
    connectorActivities,
    loading: isLoading,
    error: (error as Error | null) ?? null,
  };
};

// ── Videos (global, by-id) ──────────────────────────────────────────────────

type VideosFile = { publishedAt: string; videos: CMSUploadedVideo[] };

export interface UseVideoContentResult {
  video: CMSUploadedVideo | undefined;
  loading: boolean;
  error: Error | null;
}

/** Static replacement for `useVideoContent(id)` — by-id lookup over one file. */
export const useVideoContent = (id: string): UseVideoContentResult => {
  const { pointers } = usePointers();
  const key = pointers?.videos;
  const { data, isLoading, error } = useQuery<VideosFile>({
    queryKey: ["s3", "videos", key],
    queryFn: () => fetchStaticJson<VideosFile>(key as string),
    enabled: !!key,
    ...IMMUTABLE_RQ,
  });
  useReactQueryErrorReporter("Could not load the Video content", error, false);
  const video = useMemo(
    () =>
      id
        ? (data?.videos ?? []).find((v) => String(v.id) === String(id))
        : undefined,
    [data, id],
  );
  return { video, loading: isLoading, error: (error as Error | null) ?? null };
};

// ── Awards (global; filter by plan OR earned) ───────────────────────────────

type AwardsFile = { publishedAt: string; awards: CMSAward[] };

export interface UseAwardsResult {
  awards: CMSAward[];
  allAwards: CMSAward[];
  loading: boolean;
  error: Error | null;
}

/**
 * Static replacement for `useAwards`. Loads the single global awards file and
 * applies the same filter as the GraphQL hook: an award shows if the climber's
 * plans match the award's `productPlan` codes OR the award is already earned
 * (so earned awards survive plan changes). `allAwards` is the unfiltered list.
 */
export const useAwards = (earnedAwardIds?: string[]): UseAwardsResult => {
  const { matchesProductPlans } = useProductFeatures();
  const { pointers } = usePointers();
  const key = pointers?.awards;
  const { data, isLoading, error } = useQuery<AwardsFile>({
    queryKey: ["s3", "awards", key],
    queryFn: () => fetchStaticJson<AwardsFile>(key as string),
    enabled: !!key,
    ...IMMUTABLE_RQ,
  });
  useReactQueryErrorReporter("Could not load the list of Awards", error, false);

  const earnedSet = useMemo(
    () => new Set(earnedAwardIds ?? []),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [earnedAwardIds?.join(",")],
  );
  const allAwards = useMemo(() => data?.awards ?? [], [data]);
  const awards = useMemo(
    () =>
      allAwards.filter(
        (award) =>
          matchesProductPlans(award.productPlan?.map((p) => p.code) || []) ||
          earnedSet.has(String(award.id)),
      ),
    [allAwards, matchesProductPlans, earnedSet],
  );

  return {
    awards,
    allAwards,
    loading: isLoading,
    error: (error as Error | null) ?? null,
  };
};

// ── Facets + facet types (one combined file, shared cache) ──────────────────

type FacetsIndexFile = {
  planCode: string;
  publishedAt: string;
  facetTypes: CMSClimberFacetType[];
  facets: CMSClimberFacet[];
};

/**
 * Internal: resolve the user's product plans → their per-plan facets index
 * files (`pointers.facetIndexes[plan]`), fetch in parallel, and merge/dedupe
 * facet types + facets by id. Both `useFacetTypes` and `useFacets`
 * call this with the SAME query key, so it's one fetch / one shared cache entry.
 */
const useFacetsIndexFile = (): {
  facetTypes: CMSClimberFacetType[];
  facets: CMSClimberFacet[];
  isLoading: boolean;
  error: Error | null;
} => {
  const { files, isLoading, error } = usePlanScopedFiles<FacetsIndexFile>(
    "facetIndexes",
    "facetIndexes",
  );

  const facetTypes = useMemo<CMSClimberFacetType[]>(
    () =>
      mergeById(
        files,
        (f) => f.facetTypes,
        (ft) => ft.id,
      ),
    [files],
  );

  const facets = useMemo<CMSClimberFacet[]>(
    () =>
      mergeById(
        files,
        (f) => f.facets,
        (fa) => fa.id,
      ),
    [files],
  );

  return {
    facetTypes,
    facets,
    isLoading,
    error,
  };
};

export interface UseFacetTypesResult {
  facetTypes: CMSClimberFacetType[];
  loading: boolean;
  error: Error | null;
}

/** Static replacement for `useFacetTypes` — merged over the user's plan files. */
export const useFacetTypes = (): UseFacetTypesResult => {
  const { facetTypes, isLoading, error } = useFacetsIndexFile();
  useReactQueryErrorReporter(
    "Could not load the list of Facet Types",
    error,
    false,
  );
  return { facetTypes, loading: isLoading, error };
};

export interface UseFacetsResult {
  facets: CMSClimberFacet[];
  loading: boolean;
  error: Error | null;
}

/** Static replacement for `useFacets` — merged over the user's plan files. */
export const useFacets = (): UseFacetsResult => {
  const { facets, isLoading, error } = useFacetsIndexFile();
  useReactQueryErrorReporter("Could not load the list of Facets", error, false);
  return { facets, loading: isLoading, error };
};

// ── Educator downloads (per plan; merge over user's plans) ──────────────────

type EducatorDownloadsIndexFile = {
  planCode: string;
  publishedAt: string;
  educatorDownloads: Array<CMSEducatorDownload & { downloadTags?: string[] }>;
};

/**
 * Resolve the user's product plans → their per-plan educator-downloads index
 * files (`pointers.educatorDownloadIndexes[plan]`), fetch in parallel, and
 * merge/dedupe downloads by id.
 */
const useEducatorDownloadsFiles = (): {
  files: EducatorDownloadsIndexFile[];
  isLoading: boolean;
  error: Error | null;
} => {
  const { files, isLoading, error } =
    usePlanScopedFiles<EducatorDownloadsIndexFile>(
      "educatorDownloadIndexes",
      "educatorDownloadIndexes",
    );
  return { files: files ?? [], isLoading, error };
};

export interface UseEducatorDownloadsResult {
  educatorDownloads: CMSEducatorDownload[];
  isLoading: boolean;
  error: Error | null;
}

/**
 * Static replacement for `useGetEducatorDownloads`. Merges the user's per-plan
 * index files, then mirrors the old hook's post-processing: maps `downloadTags`
 * → `tags` and filters each download's `careerClusters` to clusters matching
 * the climber's plans.
 */
export const useGetEducatorDownloads = (): UseEducatorDownloadsResult => {
  const { files, isLoading, error } = useEducatorDownloadsFiles();
  const { matchesProductPlans } = useProductFeatures();
  useReactQueryErrorReporter("Could not load educator downloads", error, false);

  const educatorDownloads = useMemo<CMSEducatorDownload[]>(() => {
    const seen = new Set<string>();
    const out: CMSEducatorDownload[] = [];
    for (const file of files) {
      for (const dl of file.educatorDownloads) {
        const id = String(dl.id);
        if (seen.has(id)) continue;
        seen.add(id);
        out.push({
          ...dl,
          tags: dl.downloadTags || [],
          careerClusters: dl.careerClusters?.filter((cluster) =>
            matchesProductPlans(
              cluster.productPlan?.map((plan) => plan.code) || [],
            ),
          ),
        });
      }
    }
    return out;
  }, [files, matchesProductPlans]);

  return { educatorDownloads, isLoading, error };
};

// ── Reports (per plan; merge over user's plans) ─────────────────────────────

type ReportsIndexFile = {
  planCode: string;
  publishedAt: string;
  reports: CMSReport[];
};

export interface UseReportsResult {
  reports: CMSReport[];
  loading: boolean;
  error: Error | null;
}

/**
 * Static replacement for `useCMSReports`. Resolves the user's product plans →
 * their per-plan reports index files, fetches in parallel, and merges/dedupes
 * by id. The files are already plan-filtered, so no client-side plan filter is
 * needed (consumers still filter by `availableRoles`).
 */
export const useCMSReports = (): UseReportsResult => {
  const { files, isLoading, error } = usePlanScopedFiles<ReportsIndexFile>(
    "reportIndexes",
    "reportIndexes",
  );
  useReactQueryErrorReporter(
    "Could not load the list of Reports",
    error,
    false,
  );

  const reports = useMemo<CMSReport[]>(
    () =>
      mergeById(
        files,
        (f) => f.reports,
        (r) => r.id,
      ),
    [files],
  );

  return {
    reports,
    loading: isLoading,
    error,
  };
};

// ── Badges catalog (per plan; merged over user's plans) ─────────────────────

type BadgeIndexFile = {
  planCode: string;
  publishedAt: string;
  badges: CMSBadge[];
};

export interface UseBadgeCatalogResult {
  badges: CMSBadge[];
  loading: boolean;
  error: Error | null;
}

/**
 * Static replacement for the CMS-catalog half of `useBadges`. Resolves the
 * user's product plans → their per-plan badge index files
 * (`pointers.badgeIndexes[plan]`), fetches in parallel, and merges/dedupes by
 * id. Earned/progress data is NOT here — `useBadges` merges that in from
 * GraphQL. Returns the raw `CMSBadge[]` catalog.
 */
export const useBadgeCatalog = (): UseBadgeCatalogResult => {
  const { files, isLoading, error } = usePlanScopedFiles<BadgeIndexFile>(
    "badgeIndexes",
    "badgeIndexes",
  );

  const badges = useMemo<CMSBadge[]>(
    () =>
      mergeById(
        files,
        (f) => f.badges,
        (b) => b.id,
      ),
    [files],
  );

  return {
    badges,
    loading: isLoading,
    error,
  };
};

// ── Banners (global; client filters by active/date/path/plan) ───────────────

type BannersFile = { publishedAt: string; banners: CMSBanner[] };

/**
 * Convert a CMS path pattern to a RegExp.
 * - `**` matches any number of path segments (including zero)
 * - `*` matches exactly one path segment
 */
const pathPatternToRegex = (pattern: string): RegExp => {
  const escaped = pattern
    .replace(/[.+?^${}()|[\]\\]/g, "\\$&")
    .replace(/\*\*/g, "<<GLOBSTAR>>")
    .replace(/\*/g, "[^/]+")
    .replace(/<<GLOBSTAR>>/g, ".*");
  return new RegExp(`^${escaped}$`);
};

const matchesPath = (patterns: string[] | null, pathname: string): boolean => {
  if (!patterns || patterns.length === 0) return true;
  return patterns.some((pattern) => pathPatternToRegex(pattern).test(pathname));
};

const isWithinDateRange = (
  startDate: string | null,
  endDate: string | null,
): boolean => {
  const now = Date.now();
  if (startDate && new Date(startDate).getTime() > now) return false;
  if (endDate && new Date(endDate).getTime() < now) return false;
  return true;
};

/**
 * Static replacement for `useBanners`. Loads the single global banners file and
 * applies the same client-side filtering: active, date range, path match, and
 * product plans. Maps to the `BannerData` shape consumed by `BannerDisplay`.
 */
export const useBanners = (): {
  banners: BannerData[];
  loading: boolean;
} => {
  const pathname = usePathname();
  const { matchesProductPlans } = useProductFeatures();
  const { pointers } = usePointers();
  const key = pointers?.banners;
  const { data, isLoading, error } = useQuery<BannersFile>({
    queryKey: ["s3", "banners", key],
    queryFn: () => fetchStaticJson<BannersFile>(key as string),
    enabled: !!key,
    ...IMMUTABLE_RQ,
  });
  useReactQueryErrorReporter("Could not load banners", error, false);

  const banners = useMemo<BannerData[]>(() => {
    const all = data?.banners ?? [];
    return all
      .filter((b) => {
        if (!b.active) return false;
        if (!isWithinDateRange(b.startDate, b.endDate)) return false;
        if (!matchesPath(b.paths, pathname)) return false;
        if (
          b.productPlans &&
          b.productPlans.length > 0 &&
          !matchesProductPlans(b.productPlans)
        )
          return false;
        return true;
      })
      .map((b) => ({
        id: b.id,
        title: b.title,
        description: b.description,
        image: b.image,
      }));
  }, [data, pathname, matchesProductPlans]);

  return { banners, loading: isLoading };
};

// ── Career plan next steps (global) ─────────────────────────────────────────

type CareerPlanNextStepsFile = {
  publishedAt: string;
  careerPlanNextSteps: CMSCareerPlanNextStep[];
};

export interface UseCareerPlanNextStepsResult {
  careerPlanNextSteps: CMSCareerPlanNextStep[];
  loading: boolean;
  error: Error | null;
}

/**
 * Static replacement for `useCareerPlanNextSteps`. Loads the single global
 * file; no plan filtering (consumers filter by pathway at runtime).
 */
export const useCareerPlanNextSteps = (): UseCareerPlanNextStepsResult => {
  const { pointers } = usePointers();
  const key = pointers?.careerPlanNextSteps;
  const { data, isLoading, error } = useQuery<CareerPlanNextStepsFile>({
    queryKey: ["s3", "careerPlanNextSteps", key],
    queryFn: () => fetchStaticJson<CareerPlanNextStepsFile>(key as string),
    enabled: !!key,
    ...IMMUTABLE_RQ,
  });
  useReactQueryErrorReporter(
    "Could not load career plan next steps",
    error,
    false,
  );

  return {
    careerPlanNextSteps: data?.careerPlanNextSteps ?? [],
    loading: isLoading,
    error: (error as Error | null) ?? null,
  };
};

// ── CMS regions taxonomy (global; split by type) ────────────────────────────

type RegionsFile = { publishedAt: string; regions: CMSRegion[] };

export interface UseRegionsResult {
  countries: CMSRegion[];
  states: CMSRegion[];
  msas: CMSRegion[];
  loading: boolean;
  error: Error | null;
}

/**
 * Static replacement for `useRegions`. Loads the single global regions file and
 * splits it by `type` into countries/states/msas (same shape as the old hook).
 */
export const useRegions = (): UseRegionsResult => {
  const { pointers } = usePointers();
  const key = pointers?.regions;
  const { data, isLoading, error } = useQuery<RegionsFile>({
    queryKey: ["s3", "regions", key],
    queryFn: () => fetchStaticJson<RegionsFile>(key as string),
    enabled: !!key,
    ...IMMUTABLE_RQ,
  });
  useReactQueryErrorReporter(
    "Could not load the list of regions",
    error,
    false,
  );

  const cmsRegions = useMemo(() => data?.regions ?? [], [data]);
  const countries = useMemo(
    () => cmsRegions.filter((r) => r.type === "country"),
    [cmsRegions],
  );
  const states = useMemo(
    () => cmsRegions.filter((r) => r.type === "state"),
    [cmsRegions],
  );
  const msas = useMemo(
    () => cmsRegions.filter((r) => r.type === "msa"),
    [cmsRegions],
  );

  return {
    countries,
    states,
    msas,
    loading: isLoading,
    error: (error as Error | null) ?? null,
  };
};

export const getAssessmentForLadder = (
  cmsLadder: CMSLadderFull | null | undefined,
  previousActivityReferenceId?: string,
): CMSAssessment | undefined => {
  if (previousActivityReferenceId) {
    const assessment = cmsLadder?.assessments?.find(
      (a) => a.assessment === previousActivityReferenceId,
    );
    if (assessment) {
      if (!assessment.active) {
        console.warn(
          `Assessment ${assessment.assessment} is not active but this climber previously started it, so using that one.`,
        );
      }
      return assessment;
    }
    console.error(
      `Climber previously started assessment ${previousActivityReferenceId} but it is not in the ladder ${cmsLadder?.id}`,
    );
  }

  const activeAssessment = cmsLadder?.assessments?.find((a) => a.active);
  if (!activeAssessment) {
    return cmsLadder?.assessments?.[0];
  }
  return activeAssessment;
};
